import { TUNABLES } from "../config";
import { jsonRecord, MAX_ID_CHARS, MAX_SESSION_CHARS, oneOf, text } from "./protocol";

/**
 * Google sign-in on the wire (B3.10.1, D-077): what the page posts to the Worker's `/signin` and
 * what it answers. Shared by the page (`src/front/signIn.ts`) and the Worker
 * (`server/cloudflare/signInEndpoint.ts`), so both read one shape. The page brings Google's ID
 * token from the redirect and the nonce it asked Google to put in it; the Worker checks the token
 * itself (`server/googleToken.ts`) and answers with the account's stable ID, a keyed hash of
 * Google's `sub` that only the Worker can make (D-078), never the `sub` itself.
 */
export interface SignInRequest {
  readonly token: string;
  readonly nonce: string;
}

/** Why the Worker turned a token down; "setup" is a Worker without its client ID or its key. */
export const SIGN_IN_REFUSALS = [
  "shape",
  "algorithm",
  "key",
  "signature",
  "issuer",
  "audience",
  "time",
  "nonce",
  "subject",
  "setup",
] as const;

export type SignInRefusal = (typeof SIGN_IN_REFUSALS)[number];

/**
 * The account's code; since NM2, where the Worker keeps names (`NAMES`), its session (the seal a
 * claim and a join show, `server/session.ts`) and the name the account holds, if it holds one.
 */
export type SignInAnswer =
  | {
      readonly account: string;
      readonly session?: string;
      readonly name?: string;
    }
  | { readonly refused: SignInRefusal };

/** Every account ID starts so: the events' queries tell an account from a guest by it. */
export const ACCOUNT_PREFIX = "google:";

/** A nonce the page makes: base64url of `TUNABLES.signIn.randomBytes`, with room to spare. */
const MAX_NONCE_CHARS = 128;

/** A request from a page, checked; undefined when it is not one. */
export function parseSignInRequest(raw: string): SignInRequest | undefined {
  if (raw.length > TUNABLES.signIn.maxRequestChars) {
    return undefined;
  }
  const value = jsonRecord(raw);
  const token = text(value?.token, TUNABLES.signIn.maxRequestChars);
  const nonce = text(value?.nonce, MAX_NONCE_CHARS);
  return token && nonce ? { token, nonce } : undefined;
}

/** The Worker's answer, checked; undefined for anything else (an older Worker, a proxy's page). */
export function parseSignInAnswer(raw: string): SignInAnswer | undefined {
  const value = jsonRecord(raw);
  const account = text(value?.account, MAX_ID_CHARS);
  if (account?.startsWith(ACCOUNT_PREFIX)) {
    const session = text(value?.session, MAX_SESSION_CHARS);
    const name = text(value?.name, TUNABLES.landing.nameMaxLength);
    return {
      account,
      ...(session === undefined ? {} : { session }),
      ...(name === undefined ? {} : { name }),
    };
  }
  const refused = oneOf(SIGN_IN_REFUSALS, value?.refused);
  return refused ? { refused } : undefined;
}

/** The sign-in endpoint beside a lobby server: `wss://host` becomes `https://host/signin`. */
export function signInUrlOf(lobbyUrl: string): string {
  return `${lobbyUrl.replace(/^ws(s?):\/\//, "http$1://")}/signin`;
}
